import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";
import { FiRefreshCw } from "react-icons/fi";
import {
  ChunkReloadDeps,
  isChunkLoadError,
  reloadOnceOnChunkError,
} from "src/utils/reloadOnChunkError";

interface Props {
  children: ReactNode;
  /** Test seam — production omits this and uses sessionStorage + location.reload. */
  reloadDeps?: ChunkReloadDeps;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for catching chunk loading failures.
 * When a new deployment happens and users have cached old JS,
 * this shows a friendly "Update Available" message instead of a crash.
 *
 * GZ#2985: never re-throw from render() (React re-enters this same boundary)
 * and never remount the failed children (that retries the lazy import
 * unboundedly). Both of those end in RangeError: Maximum call stack size
 * exceeded — the #163 / #2031 storm after a deploy.
 */
class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, isChunkError: isChunkLoadError(error), error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error for monitoring
    console.error("ChunkErrorBoundary caught error:", error, errorInfo);

    // One sessionStorage-guarded reload. Use the caught error, not this.state
    // (state can still be the pre-GDSFE value here). A second miss in this
    // tab does not reload again — a still-propagating deploy must not loop.
    reloadOnceOnChunkError(error, this.props.reloadDeps);
  }

  handleRefresh = (force?: boolean) => {
    if ((window as any).forceRefresh) {
      (window as any).forceRefresh(force);
    } else {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // Chunk miss: auto-reload is in flight. Still render a manual escape
      // hatch in case the refresh was suppressed or the reload itself
      // serves stale assets. Do not throw and do not remount children.
      if (this.state.isChunkError) {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
              bgcolor: "#F5F7FA",
            }}
          >
            <FiRefreshCw size={32} color="#22356D" className="animate-spin" />
            <Typography
              sx={{ color: "#22356D", fontFamily: "var(--font-regular)" }}
            >
              Updating to the latest version…
            </Typography>
            <Button
              onClick={() => this.handleRefresh(true)}
              variant="contained"
              sx={{
                background: "#22356D",
                textTransform: "none",
                borderRadius: "10px",
                "&:hover": { background: "#1a2b55" },
              }}
            >
              Refresh Now
            </Button>
          </Box>
        );
      }

      // Non-chunk: swallow here. Re-throwing from this render() is what
      // overflowed the stack (GZ#2985). Remounting children retries the
      // failed tree unboundedly. A refresh button is the way out.
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            bgcolor: "#F5F7FA",
          }}
        >
          <Button
            onClick={() => this.handleRefresh(true)}
            variant="contained"
            sx={{
              background: "#22356D",
              textTransform: "none",
              borderRadius: "10px",
              "&:hover": { background: "#1a2b55" },
            }}
          >
            Refresh Now
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
